/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2014-2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

angular.module('bulbApp.unitModule', ['focus-if', 'restangular', 'ui.router', 'bulbApp.authModule',
  'bulbApp.busyModule', 'bulbApp.configModule', 'bulbApp.errorModule', 'bulbApp.translateModule'])
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  .config(['$stateProvider', 'AUTH', config]);

function config($stateProvider, AUTH) {
  // Defining the 'createCollection' state information here
  // so it can be used by both the createCollection and the createSubCollection states below.
  const createCollection = {
    parent: 'root',
    resolve: {
      unit() {},
      $title: ['$translate', ($translate) => $translate('create_collection')],
    },
    views: {
      content: {
        templateUrl: 'ajs/unit/createcollection.html',
        controller: 'CreateCollectionController as createCollectionCtrl',
      },
    },
    bulbAuth: {
      isAuthorized: ['BulbAuthService', (BulbAuthService) => BulbAuthService.currentUser.canCreate('Collection')],
    },
  };

  $stateProvider
    .state('unit', {
      parent: 'root',
      url: '^/u/{unitPath:pathVariable}?published&sharedLink&uid&context',
      params: {
        uid: null,
        context: null,
      },
      resolve: {
        // This resolve is almost identical to that of 'live_page' below, so any changes here should
        // probably be duplicated there as well.
        // false parameter below => don't force the loading of published versions of pages, which is the
        // standard behavior, allowing an author to see the draft version of pages by default.  Forcing
        // the published versions of pages is used for is used below in 'live_page'.
        unit: ['$stateParams', '$location', 'BulbAjaxService', function unit($stateParams, $location, BulbAjaxService) {
          return BulbAjaxService
            .getUnit($stateParams.unitPath, false, $location.search().sharedLink)
            .then((data) => data.unitDTOs[0]);
        }],
        $title: ['unit', (unit) => unit.name],
      },
      data: {
        mode: 'default',
      },
      bulbAuth: {
        authorizedRoles: [AUTH.ROLES.USER],
      },
      onEnter: [
        'unit', '$location', '$state', '$stateParams', '$timeout', '$window',
        'BulbAuthService', 'BulbUnitService', 'BulbPresentationService',
        (
          unit,
          $location,
          $state,
          $stateParams,
          $timeout,
          $window,
          BulbAuthService,
          BulbUnitService,
          BulbPresentationService,
        ) => {
          BulbUnitService.setCurrentUnit(unit);
          const { context } = $stateParams;

          // If the user is authenticated successfully then set the 'isAuthorized' key in localstorage
          if ($stateParams.uid === unit.id && BulbAuthService.currentUser.isAuthenticated()) {
            // eslint-disable-next-line no-param-reassign
            $stateParams.uid = null;
            localStorage.setItem('isAuthorized', 'isAuthorized');
            $window.history.replaceState({}, '', `/u/${$stateParams.unitPath}`);
          }

          // if the unit has a newer path than the path used to load it, update the url to use it instead:
          if ($stateParams.unitPath.toLowerCase() !== unit.path.toLowerCase()) {
            $location.skipReload().path(`/u/${unit.path}`).replace();
            // eslint-disable-next-line no-param-reassign
            $stateParams.unitPath = unit.path;
          }

          // Redirect to the appropriate child state based on the type of the unit that was resolved.
          // Use $timeout so that the current state change completely finishes before transitioning to the new
          // child state.
          $timeout(() => {
            // Adding this check here so that if a user presents from the collection level, we essentially have
            // two states that are going to get hit. This state, the unit state, and the presentation state.
            // So if a user presents from the collection level, we check that if the current unit's
            // ancestor is that of a collection -- composite unit. If this is the case, we then correctly
            // transition them to the presentation state.
            // Important thing to note is that this is a *TEMPORARY* solution until we update to ng2. It is
            // temporary because we have to explicitly use the `:[^/]*` filter to allow for the encoding
            // of the double `~~`. However in doing this, it makes this url path and the presentation_page path
            // somewhat indistinguishable -- ambiguous. Therefore the conditional check is necessary.
            if (BulbPresentationService.isFullScreen() && unit.ancestorUnits[1].unitType === 'CompositeUnit') {
              $state.go('presentation_page', {
                unitPath: unit.path,
              });
            } else if (unit.unitType === 'CompositeUnit' || unit.unitType === 'ArchiveCompositeUnit') {
              $state.go('unit.collection');
            } else if (unit.unitType === 'AssignmentCompositeUnit') {
              $state.go('unit.collection', {
                context: 'assignment',
              });
            } else if (context && context === 'assignment') {
              $state.go('unit.page', {
                context: 'assignment',
              });
            } else {
              $state.go('unit.page');
            }
          });
        }],
      onExit: ['BulbUnitService', (BulbUnitService) => {
        BulbUnitService.setCurrentUnit(null);
      }],
    })
    .state('unit.page', {
      resolve: {
        $title: ['$title', '$translate', ($title, $translate) => {
          // If the 'unit' state failed to provide a $title, the page must not have one, so use an unnamed
          // title instead.
          // Note: only unpublished pages can have titles, so we don't need to do the same
          // down below in live_page.
          if ($title) {
            return $title;
          }
          return $translate('pageview_unnamed_title');
        }],
      },
      views: {
        // These views are identical to that of 'live_page' below, so any changes here should be duplicated
        // there as well.
        'header@root': {
          templateUrl: 'ajs/components/pageview/pageviewheader.html',
          controller: 'PageViewHeaderController as pageViewHeaderCtrl',
        },
        'content@root': {
          templateUrl: 'ajs/unit/page.html',
          controller: 'PageController as pageCtrl',
        },
        'footer@root': {
          templateUrl: 'ajs/components/pageview/pageviewfooter.html',
          controller: 'PageViewFooterController as pageViewFooterCtrl',
        },
      },

    })
    .state('unit.collection', {
      views: {
        'content@root': {
          templateUrl: 'ajs/unit/collection.html',
          controller: ['unit', '_', function collectionRootCtrl(unit, _) {
            this.unit = unit;
            this.infoType = this.unit.unitType === 'AssignmentCompositeUnit' ? 'assignment' : 'collection';
          }],
          controllerAs: 'collectionCtrl',
        },
        'footer@root': {
          templateUrl: 'ajs/components/compositeview/compositeviewfooter.html',
          controller: ['unit', 'BulbUnitService', function collectionFooterCtrl(unit, BulbUnitService) {
            this.unit = unit;
            BulbUnitService.fetchParent().then(() => {
              this.parentUnit = BulbUnitService.parentUnit;
              this.previousUnit = BulbUnitService.previousSibling;
              this.nextUnit = BulbUnitService.nextSibling;
            });
          }],
          controllerAs: 'compositeViewFooterCtrl',
        },
      },
      onEnter: ['$cookies', 'RecordEventService', ($cookies, RecordEventService) => {
        const preferences = $cookies.get('prefs') ? JSON.parse($cookies.get('prefs')) : {};
        const collectionViewOption = preferences.useTileViewCollection ? 'tile' : 'list';
        if (preferences.useTileViewCollection === true || preferences.useTileViewCollection === false) {
          RecordEventService
            .recordEvent('view_collection', 'view_option', collectionViewOption);
        } else {
          RecordEventService.recordEvent('view_collection', 'view_option', 'not_set');
        }
      }],
    })
    // The live_page state is for displaying the currently published version of a page for review by thea author
    // of the page.  By default, when not using this state, the author sees the latest draft version of a page.
    .state('live_page', {
      parent: 'root',
      url: '^/u/{unitPath:pathVariable}/live',
      resolve: {
        unit: ['$stateParams', 'BulbAjaxService', ($stateParams, BulbAjaxService) => {
          // This resolve is almost identical to that of 'unit' above, so any changes here should probably be
          // duplicated there as well.
          const usePublishedVersion = true;
          return BulbAjaxService.getUnit($stateParams.unitPath, usePublishedVersion).then((data) => {
            const unit = data.unitDTOs[0];
            return unit;
          });
        }],
        $title: ['unit', (unit) => unit.name],
      },
      views: {
        // These views are identical to that of 'unit.page' above, so any changes here should be duplicated
        // there as well.
        'header@root': {
          templateUrl: 'ajs/components/pageview/pageviewheader.html',
          controller: 'PageViewHeaderController as pageViewHeaderCtrl',
        },
        'content@root': {
          templateUrl: 'ajs/unit/page.html',
          controller: 'PageController as pageCtrl',
        },
        'footer@root': {
          templateUrl: 'ajs/components/pageview/pageviewfooter.html',
          controller: 'PageViewFooterController as pageViewFooterCtrl',
        },
      },
      data: {
        mode: 'live',
      },
      bulbAuth: {
        authorizedRoles: [AUTH.ROLES.USER],
      },
      onEnter: ['unit', 'BulbUnitService', (unit, BulbUnitService) => {
        BulbUnitService.setCurrentUnit(unit);
      }],
      onExit: ['BulbUnitService', (BulbUnitService) => {
        BulbUnitService.setCurrentUnit(null);
      }],
    })
    // TODO CLEAN : perhaps 'preview_page' can be made into a child of unit.page or live_page, so that it can
    // inherit from one of them considering the only difference between this and unit.page is the data.mode, and
    // the only differences to live_page are usePublishedVersion and data.mode.
    .state('preview_page', {
      parent: 'root',
      url: '^/u/{unitPath:pathVariable}/preview',
      resolve: {
        unit: ['$stateParams', 'BulbAjaxService', ($stateParams, BulbAjaxService) => {
          // This resolve is almost identical to that of 'unit' above, so any changes here should probably be
          // duplicated there as well.
          const usePublishedVersion = false;
          return BulbAjaxService.getUnit($stateParams.unitPath, usePublishedVersion).then((data) => {
            const unit = data.unitDTOs[0];
            return unit;
          });
        }],
        $title: ['unit', (unit) => unit.name],
      },
      views: {
        // These views are identical to that of 'unit.page' above, so any changes here should be duplicated
        // there as well.
        'header@root': {
          templateUrl: 'ajs/components/pageview/pageviewheader.html',
          controller: 'PageViewHeaderController as pageViewHeaderCtrl',
        },
        'content@root': {
          templateUrl: 'ajs/unit/page.html',
          controller: 'PageController as pageCtrl',
        },
        'footer@root': {
          templateUrl: 'ajs/components/pageview/pageviewfooter.html',
          controller: 'PageViewFooterController as pageViewFooterCtrl',
        },
      },
      data: {
        mode: 'preview',
      },
      bulbAuth: {
        authorizedRoles: [AUTH.ROLES.USER],
      },
      onEnter: ['unit', 'BulbUnitService', (unit, BulbUnitService) => {
        BulbUnitService.setCurrentUnit(unit);
      }],
      onExit: ['BulbUnitService', (BulbUnitService) => {
        BulbUnitService.setCurrentUnit(null);
      }],
    })
    .state('snapshot_preview', {
      parent: 'root',
      url: '^/u/{unitPath:pathVariable}/snapshot?snapshotId',
      params: {
        draftPageId: null,
        snapshotId: null,
        timestamp: null,
        snapshots: null,
        previous: null,
        next: null,
      },
      resolve: {
        unit: ['$stateParams', 'Restangular',
          ($stateParams, Restangular) => Restangular
            .one('units', $stateParams.draftPageId)
            .one('snapshots', $stateParams.snapshotId)
            .get()
            .then((data) => {
              const unit = data;
              return unit;
            })],
        $title: ['unit', (unit) => unit.name],
      },
      views: {
        'header@root': {
          templateUrl: 'ajs/components/pageview/pageviewsnapshotheader.html',
          controller: 'PageViewHeaderController as pageViewHeaderCtrl',
        },
        'content@root': {
          templateUrl: 'ajs/unit/page.html',
          controller: 'PageController as pageCtrl',
        },
        'footer@root': {
          templateUrl: 'ajs/components/pageview/pageviewfooter.html',
          controller: 'PageViewFooterController as pageViewFooterCtrl',
        },
      },
      data: {
        mode: 'preview',
        snapshotView: true,
      },
      bulbAuth: {
        authorizedRoles: [AUTH.ROLES.USER],
      },
      onEnter: ['unit', 'BulbUnitService', (unit, BulbUnitService) => {
        BulbUnitService.setCurrentUnit(unit);
      }],
      onExit: ['BulbUnitService', (BulbUnitService) => {
        BulbUnitService.setCurrentUnit(null);
      }],
    })
    .state('unit.page.comment', {
      parent: 'root',
      url: '^/u/{unitPath:pathVariable}/comment/{threadId}',
      onEnter: ['BulbCommentService', '$stateParams', '$location', (BulbCommentService, $stateParams, $location) => {
        BulbCommentService.setDefaultThread($stateParams.threadId);
        $location.url(`/u/${$stateParams.unitPath}`);
      }],
    })
    .state('presentation_page', {
      parent: 'root',
      url: '^/u/{unitPath:pathVariable}/present?{usePublishedVersion}',
      resolve: {
        unit: ['$stateParams', 'BulbAjaxService', ($stateParams, BulbAjaxService) => {
          // Handling the $stateParams.usePublishedVersion value below such that we actually return a boolean
          // value, rather than a string.
          const usePublishedVersion = ($stateParams.usePublishedVersion === 'true');
          return BulbAjaxService.getUnit($stateParams.unitPath, usePublishedVersion).then((data) => {
            const unit = data.unitDTOs[0];
            return unit;
          });
        }],
        $title: ['unit', (unit) => unit.name],
      },
      views: {
        'content@root': {
          templateUrl: 'ajs/unit/page.html',
          controller: 'PageController as pageCtrl',
        },
        'footer@root': {
          template: '<bulb-presentation-footer></bulb-presentation-footer>',
        },
      },
      data: {
        mode: 'presentation',
      },
      bulbAuth: {
        authorizedRoles: [AUTH.ROLES.USER],
      },
      onEnter: ['unit', 'BulbUnitService', (unit, BulbUnitService) => {
        BulbUnitService.setCurrentUnit(unit);
      }],
      onExit: ['BulbUnitService', (BulbUnitService) => {
        BulbUnitService.setCurrentUnit(null);
      }],
    })
    .state('createCollection', angular.merge({}, createCollection, {
      url: '^/b/collection/create',
    }))
    .state('createSubCollection', angular.merge({}, createCollection, {
      url: '^/u/{unitPath:pathVariable}/collection/create',
      resolve: {
        unit: ['$stateParams', 'BulbAjaxService',
          ($stateParams, BulbAjaxService) => BulbAjaxService.getUnit($stateParams.unitPath, false)
            .then((data) => data.unitDTOs[0]),
        ],
      },
      onEnter: ['$state', 'BulbUnitService', 'BulbSubCollectionService', 'BulbAuthService', 'unit',
        // These 400 error redirects should probably be resolved before entering the route,
        // with a consistent return type.
        // eslint-disable-next-line consistent-return
        ($state, BulbUnitService, BulbSubCollectionService, BulbAuthService, unit) => {
          BulbUnitService.setCurrentUnit(unit);
          if (!BulbSubCollectionService.isSubCollectionCountUnderLimit()) {
            return $state.go('notFound', {}, { location: false });
          }
          if (!BulbAuthService.currentUser.features.SubCollectionFeature.enabled) {
            return $state.go('accessDenied', {}, { location: false });
          }
        },
      ],
      onExit: ['BulbUnitService',
        (BulbUnitService) => {
          BulbUnitService.setCurrentUnit(null);
        },
      ],
    }));
}
